import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import React, { useEffect, useMemo, useState } from 'react'
import { groupBy, orderBy, uniqBy, keys } from 'lodash'
import { getQualificationSelectOptions, getStatusSelectOptions } from '../../../../../utils/StatusUtil'
import SimpleFilterSelect from '../../../../../components/forms/specific/SimpleFilterSelect'
import { STATION_TYPE_CONSTANT, STATION_TYPE_NAME } from '../../../../../station/constants/StationConstants'
import Input from '../../../../../components/forms/Input'
import i18n from 'simple-react-i18n'
import SimpleDatePicker from '../../../../../components/forms/SimpleDatePicker'
import MultiContributorsAutocomplete
    from '../../../../../referencial/components/contributor/components/MultiContributorsAutocomplete'
import MultiContactsAutocomplete
    from '../../../../../referencial/components/contact/components/MultiContactsAutocomplete'
import SuperMultiAutocomplete from '../../../../../components/forms/SuperMultiAutocomplete'
import ThresholdSelect from '../../../../../components/forms/specific/ThresholdSelect'
import Checkbox from '../../../../../components/forms/Checkbox'
import { StyledFieldSet, StyledLegend } from '../../../../../components/StyledElements'
import PropTypes from 'prop-types'
import NumberField from '../../../../../components/forms/NumberField'
import Select from '../../../../../components/forms/Select'
import AdministrationAction from '../../../../../administration/actions/AdministrationAction'
import { Card, CardContent, Grid } from '@mui/material'
import { ButtonMUI } from 'components/styled/Buttons'
import AssociatedSectorSelect from 'components/forms/specific/AssociatedSectorSelect'
import AssociatedStationSelect from 'components/forms/specific/AssociatedStationSelect'
import { SHOW_SANDRE_CODE } from 'account/constants/AccountSettingsConstants'
import useAccountSetting from 'utils/customHook/useAccountSetting'
import useApplicationSetting from 'utils/customHook/useApplicationSetting'
import { getResearchTypes } from '../../../../../utils/QualityUtils'
import { RESEARCH_TYPE } from '../../../../constants/QualityConstants'

// aligne une case à cocher sur la ligne de saisie des champs voisins : `.input-field`
// porte un margin-top de 1rem et l'input fait 3rem de haut
const CHECKBOX_ALIGNED_ON_INPUT = {
    display: 'flex',
    alignItems: 'center',
    marginTop: '1rem',
    minHeight: '3rem',
}

const QualityIntegrationOverviewCriterias = ({
    defaultFilter = {},
    onValidate = () => { },
}) => {
    const {
        contactsProps,
        parametersOptions,
        qualityCampaigns,
        qualityJobs,
        qualitometers,
        contributors,
        networks,
        networkLinks,
        installations,
        distributionUnits,
        productionUnits,
        accountUser,
    } = useSelector(store => ({
        contactsProps: store.ContactReducer.contacts,
        parametersOptions: store.ParameterReducer.parameters,
        qualityCampaigns: store.QualityReducer.qualityCampaigns,
        qualityJobs: store.JobReducer.qualityJobs,
        qualitometers: store.QualityReducer.qualitometersLight,
        contributors: store.ContributorReducer.contributors,
        networks: store.NetworkReducer.networks,
        networkLinks: store.QualityReducer.networkLinks,
        installations: store.InstallationReducer.installationsLight,
        distributionUnits: store.DistributionUnitReducer.distributionUnits,
        productionUnits: store.ProductionUnitReducer.productionUnits,
        accountUser: store.AccountReducer.accountUser,
    }), shallowEqual)

    const generalShowSandreCode = useApplicationSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : true)
    const showSandreCode = useAccountSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : generalShowSandreCode)

    const uniqQualityJobs = uniqBy(qualityJobs, 'id')

    const [researchType, setResearchType] = useState(defaultFilter.researchType ?? RESEARCH_TYPE.WATER)

    const [sector, setSector] = useState(defaultFilter.sector)
    const [sectorAssociatedStations, setSectorAssociatedStations] = useState([])
    const [installation, setInstallation] = useState(defaultFilter.installation)
    const [installationAssociatedStations, setInstallationAssociatedStations] = useState([])
    const [productionUnit, setProductionUnit] = useState(defaultFilter.productionUnit)
    const [prodAssociatedStations, setProdAssociatedStations] = useState([])
    const [distributionUnit, setDistributionUnit] = useState(defaultFilter.distributionUnit)
    const [distriAssociatedStations, setDistriAssociatedStations] = useState([])

    const [searchValue, setSearchValue] = useState(defaultFilter.searchValue)
    const [startDate, setStartDate] = useState(defaultFilter.startDate)
    const [endDate, setEndDate] = useState(defaultFilter.endDate)
    const [producers, setProducers] = useState(defaultFilter.producers)
    const [contacts, setContacts] = useState(defaultFilter.contacts)
    const [parameters, setParameters] = useState(defaultFilter.parameters)
    const [status, setStatus] = useState(defaultFilter.status)
    const [qualification, setQualification] = useState(defaultFilter.qualification)
    const [campaign, setCampaign] = useState(defaultFilter.campaign)
    const [jobId, setJobId] = useState(defaultFilter.jobId)
    const [jobExecutionId, setJobExecutionId] = useState(defaultFilter.jobExecutionId)
    const [jobStartDate, setJobStartDate] = useState(defaultFilter.jobStartDate)
    const [jobEndDate, setJobEndDate] = useState(defaultFilter.jobEndDate)
    const [improbabilityThreshold, setImprobabilityThreshold] = useState(defaultFilter.improbabilityThreshold)
    const [impossibilityThreshold, setImpossibilityThreshold] = useState(defaultFilter.impossibilityThreshold)
    const [conformitySandre, setConformitySandre] = useState(defaultFilter.conformitySandre)
    const [outliers, setOutliers] = useState(defaultFilter.outliers)
    const [multiParam, setMultiParam] = useState(defaultFilter.multiParam)
    const [filter, setFilter] = useState(defaultFilter.filter)
    const [filterResults, setFilterResults] = useState(defaultFilter.filterResults)
    const [applyCriteriasOnStats/* , setApplyCriteriasOnStats*/] = useState(false)

    // network Filter
    const [network, setNetwork] = useState(defaultFilter.network)
    const [networkGroup, setNetworkGroup] = useState({})
    const [networkList, setNetworkList] = useState([])

    const dispatch = useDispatch()

    useEffect(() => {
        // network filter
        const networkGroupTmp = groupBy(networkLinks, 'idNetwork')
        const networkListTmp = keys(networkGroupTmp).map(nId => networks.find(n => n.id == nId)).filter(n => !!n)
        setNetworkList(networkListTmp)
        setNetworkGroup(networkGroupTmp)
    }, [])

    const statusSelectOptions = useMemo(() => getStatusSelectOptions(), [])
    const qualificationSelectOptions = useMemo(() => getQualificationSelectOptions(), [])
    const researchTypes = useMemo(getResearchTypes, [])

    const onValidateForm = () => {
        dispatch(AdministrationAction.setCache('quality', { searchValue }))
        onValidate({
            researchType,
            searchValue,
            startDate,
            endDate,
            producers,
            contacts,
            parameters,
            status,
            qualification,
            campaign,
            jobId,
            jobExecutionId,
            jobStartDate,
            jobEndDate,
            improbabilityThreshold,
            impossibilityThreshold,
            conformitySandre,
            outliers,
            multiParam,
            filter,
            filterResults,
            sector,
            sectorAssociatedStations,
            installation,
            installationAssociatedStations,
            productionUnit,
            prodAssociatedStations,
            distributionUnit,
            distriAssociatedStations,
            applyCriteriasOnStats,
            networkGroup,
            network,
        })
    }

    return (
        <Card>
            <CardContent>
                <Grid container columnSpacing={1} rowSpacing={0.5}>
                    <Grid size={2}>
                        <Select
                            label={i18n.researchType}
                            options={researchTypes}
                            value={researchType}
                            keyLabel='label'
                            keyValue='value'
                            onChange={setResearchType}
                            noSort
                            noNullValue
                        />
                    </Grid>
                    <Grid size={2}>
                        <SimpleFilterSelect
                            stationType={STATION_TYPE_NAME.quality}
                            onChange={(result, code) => {
                                setFilter(code)
                                setFilterResults(code === -1 ? [] : result)
                            }}
                            stations={qualitometers}
                        />
                    </Grid>
                    <Grid size={2}>
                        <Input
                            tooltip={i18n.searchThenEnter}
                            title={i18n.search}
                            value={searchValue}
                            onChange={setSearchValue}
                        />
                    </Grid>
                    <Grid size={2}>
                        <Select
                            label={i18n.network}
                            value={network}
                            options={networkList}
                            onChange={setNetwork}
                            keyLabel={showSandreCode ? 'labelWithSandre' : 'label'}
                            keyValue='sandreCode'
                        />
                    </Grid>
                    <Grid size={2}>
                        <SuperMultiAutocomplete
                            options={statusSelectOptions}
                            label={i18n.status}
                            values={status}
                            onChange={setStatus}
                            noSort
                        />
                    </Grid>
                    <Grid size={2}>
                        <SuperMultiAutocomplete
                            options={qualificationSelectOptions}
                            label={i18n.qualification}
                            values={qualification}
                            onChange={setQualification}
                            noSort
                        />
                    </Grid>
                    <Grid size={3}>
                        <AssociatedSectorSelect
                            value={sector}
                            onChange={(list, value) => {
                                setSector(value)
                                setSectorAssociatedStations(list)
                            }}
                            stationTypeReturn={STATION_TYPE_CONSTANT.QUALITY}
                        />
                    </Grid>
                    <Grid size={2}>
                        <AssociatedStationSelect
                            label={i18n.installation}
                            options={installations}
                            value={installation}
                            onChange={(list, value) => {
                                setInstallation(value)
                                setInstallationAssociatedStations(list)
                            }}
                            stationType={STATION_TYPE_CONSTANT.INSTALLATION}
                            stationTypeReturn={STATION_TYPE_CONSTANT.QUALITY}
                            keyValue='code'
                            displayWithCode
                        />
                    </Grid>
                    <Grid size={2}>
                        <AssociatedStationSelect
                            label={i18n.productionUnit}
                            options={productionUnits}
                            value={productionUnit}
                            onChange={(list, value) => {
                                setProductionUnit(value)
                                setProdAssociatedStations(list)
                            }}
                            stationType={STATION_TYPE_CONSTANT.PRODUCTION}
                            stationTypeReturn={STATION_TYPE_CONSTANT.QUALITY}
                            keyValue='code'
                            displayWithCode
                        />
                    </Grid>
                    <Grid size={2}>
                        <AssociatedStationSelect
                            label={i18n.distributionUnit}
                            options={distributionUnits}
                            value={distributionUnit}
                            onChange={(list, value) => {
                                setDistributionUnit(value)
                                setDistriAssociatedStations(list)
                            }}
                            stationType={STATION_TYPE_CONSTANT.DISTRIBUTION}
                            stationTypeReturn={STATION_TYPE_CONSTANT.QUALITY}
                            keyValue='code'
                            displayWithCode
                        />
                    </Grid>
                    <Grid size={3}>
                        <SuperMultiAutocomplete
                            options={qualityCampaigns}
                            label={i18n.campaign}
                            values={campaign}
                            onChange={setCampaign}
                        />
                    </Grid>
                    {/* les 2 dates occupent 1.5 colonne chacune */}
                    <Grid size={1.5}>
                        <SimpleDatePicker
                            value={startDate}
                            label={i18n.startDate}
                            onChange={setStartDate}
                            max={endDate}
                        />
                    </Grid>
                    <Grid size={1.5}>
                        <SimpleDatePicker
                            value={endDate}
                            label={i18n.endDate}
                            onChange={setEndDate}
                            min={startDate}
                        />
                    </Grid>
                    <Grid size={3}>
                        <MultiContributorsAutocomplete
                            options={contributors}
                            values={producers}
                            label={i18n.producer}
                            onChange={setProducers}
                            multiple
                        />
                    </Grid>
                    <Grid size={3}>
                        <MultiContactsAutocomplete
                            label={i18n.referent}
                            options={contactsProps}
                            onChange={setContacts}
                            values={contacts}
                            multiple
                        />
                    </Grid>
                    <Grid size={3}>
                        <SuperMultiAutocomplete
                            label={i18n.parameters}
                            options={parametersOptions}
                            limit={1}
                            onChange={setParameters}
                            values={parameters}
                            keyValue='code'
                            keyLabel={showSandreCode ? 'labelWithCode' : 'name'}
                            additionalKeysFilter={['code', 'name', 'shortLabel']}
                            multiple
                        />
                    </Grid>
                    {accountUser.isAdmin === '1' && (
                        <Grid size={12}>
                            <StyledFieldSet className='padding-1' style={{ margin: '10px 0' }}>
                                <StyledLegend>{i18n.searchByTreatment}</StyledLegend>
                                <Grid container columnSpacing={1} rowSpacing={0.5}>
                                    <Grid size={4}>
                                        <Select
                                            options={orderBy(uniqQualityJobs, 'name')}
                                            label={i18n.job}
                                            value={jobId}
                                            keyValue='id'
                                            onChange={setJobId}
                                        />
                                    </Grid>
                                    <Grid size={2}>
                                        <NumberField
                                            title={i18n.jobExecutionId}
                                            value={jobExecutionId}
                                            onChange={setJobExecutionId}
                                        />
                                    </Grid>
                                    <Grid size={3}>
                                        <SimpleDatePicker
                                            value={jobStartDate}
                                            label={`${i18n.startDate} (${i18n.executions})`}
                                            onChange={setJobStartDate}
                                            max={jobEndDate}
                                        />
                                    </Grid>
                                    <Grid size={3}>
                                        <SimpleDatePicker
                                            value={jobEndDate}
                                            label={`${i18n.endDate} (${i18n.executions})`}
                                            onChange={setJobEndDate}
                                            min={jobStartDate}
                                        />
                                    </Grid>
                                    <Grid size={3}>
                                        <ThresholdSelect
                                            title={i18n.improbability}
                                            selected={improbabilityThreshold}
                                            onChange={setImprobabilityThreshold}
                                            fieldRef='CONTROL_IMPROBABILITY'
                                        />
                                    </Grid>
                                    <Grid size={3}>
                                        <ThresholdSelect
                                            title={i18n.impossibility}
                                            selected={impossibilityThreshold}
                                            onChange={setImpossibilityThreshold}
                                            fieldRef='CONTROL_IMPOSSIBILITY'
                                        />
                                    </Grid>
                                    <Grid size={2} sx={CHECKBOX_ALIGNED_ON_INPUT}>
                                        <Checkbox
                                            label={i18n.sandreConformity}
                                            checked={conformitySandre}
                                            onChange={setConformitySandre}
                                        />
                                    </Grid>
                                    <Grid size={2} sx={CHECKBOX_ALIGNED_ON_INPUT}>
                                        <Checkbox
                                            label={i18n.outliers}
                                            checked={outliers}
                                            onChange={setOutliers}
                                        />
                                    </Grid>
                                    <Grid size={2} sx={CHECKBOX_ALIGNED_ON_INPUT}>
                                        <Checkbox
                                            label={i18n.controlMultiParam}
                                            checked={multiParam}
                                            onChange={setMultiParam}
                                        />
                                    </Grid>
                                </Grid>
                            </StyledFieldSet>
                        </Grid>
                    )}
                    <Grid size={3} offset={9}>
                        <ButtonMUI
                            onClick={onValidateForm}
                            variant='contained'
                            fullWidth
                        >
                            {i18n.search}
                        </ButtonMUI>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    )
}

QualityIntegrationOverviewCriterias.propTypes = {
    onValidate: PropTypes.func,
    defaultFilter: PropTypes.shape({}),
}

export default QualityIntegrationOverviewCriterias
